import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FiEdit2,
  FiClock,
  FiTruck,
  FiFlag,
  FiDownload,
  FiPlay,
  FiMapPin,
  FiUpload,
  FiCornerDownLeft,
  FiHome,
  FiTool,
  FiFileText,
} from 'react-icons/fi';
import Breadcrumbs from '../../components/Breadcrumbs';
import CustomModal from '../../components/CustomModal';
import FilterExternalDropdown from '../../components/FilterExternalDropdown';
import NewTableComponent from '../../components/NewTableComponent';
import activeLoadStatusIcon from '../../assets/images/Active_load.svg';
import inactiveStatusIcon from '../../assets/images/Inactive_icon.svg';
import statusUnderMaintenanceIcon from '../../assets/images/Status Under Maintenance.svg';
import AddFleetStatusEntry from './AddFleetStatusEntry';
import { apiService } from '../../services/apiService';
import { formatLocalDateTime } from '../../utils/helper';
import { plusIcon } from '../../utils/svgs';
import '../../styles/Access.css';
import '../../styles/FMCR.css';
import '../../styles/Services.css';
import '../../styles/TransportersTable.css';
import '../../styles/VehicleStatusCards.css';

// Matches /fmcr-statuses/vehicle-paginated's real `tracker_status` field (GPS/telematics live
// status) - confirmed values seen in real responses: RUNNING, STOP, INACTIVE (or null when the
// vehicle has no tracker at all, handled separately by the caller before this is invoked).
const vehicleStatusMeta = (status = '') => {
  const normalized = String(status || '').trim().toUpperCase();

  if (normalized === 'RUNNING') {
    return {
      color: '#299900',
      icon: (
        <img
          src={activeLoadStatusIcon}
          alt="Running status"
          className="transporter_status_icon_img"
        />
      ),
      label: 'Running',
    };
  }

  if (normalized === 'STOP') {
    return {
      color: '#F2841B',
      icon: (
        <img
          src={statusUnderMaintenanceIcon}
          alt="Stop status"
          className="transporter_status_icon_img"
        />
      ),
      label: 'Stop',
    };
  }

  return {
    color: '#EF0000',
    icon: (
      <img
        src={inactiveStatusIcon}
        alt="Inactive status"
        className="transporter_status_icon_img"
      />
    ),
    label: 'Inactive',
  };
};

// Real, exhaustive enums for the two fixed filters (confirmed from actual /fmcr-statuses/vehicle-paginated
// response payloads - ownership_type has 3 real values, tracker_status has 3 non-null values).
const OWNERSHIP_TYPE_FILTER_OPTIONS = [
  { label: 'Owned', value: 'owned' },
  { label: 'Hired', value: 'hired' },
  { label: '3PL', value: '3pl' },
];

const STATUS_FILTER_OPTIONS = [
  { label: 'Running', value: 'RUNNING' },
  { label: 'Stop', value: 'STOP' },
  { label: 'Inactive', value: 'INACTIVE' },
];

const VehicleStatus = () => {
  const navigate = useNavigate();
  const [vehiclesData, setVehiclesData] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [showAddEntry, setShowAddEntry] = useState(false);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [searchValue, setSearchValue] = useState('');
  const [searchFilters, setSearchFilters] = useState({});
  const [vehicleTypeOptions, setVehicleTypeOptions] = useState([]);
  const [countsData, setCountsData] = useState({
    total: 0,
    running: 0,
    idle: 0,
    stop: 0,
    inactive: 0,
  });

  // Status overview cards are computed straight from /fmcr-statuses/vehicle-paginated itself
  // (a single unfiltered, high-limit fetch covering the whole fleet), not a separate counts
  // endpoint - so the cards always agree with what this same API actually returns.
  useEffect(() => {
    let isMounted = true;
    const fetchCounts = async () => {
      try {
        const res = await apiService.getFmcrStatusesVehiclePaginated({ page: 1, limit: 5000 });
        const dataObj = res?.data || res || {};
        const results = dataObj?.results || dataObj?.rows || (Array.isArray(dataObj) ? dataObj : []);
        const total = Number(dataObj?.meta?.totalItems ?? dataObj?.total ?? results.length) || 0;

        let runCount = 0;
        let idleCount = 0;
        let stopCount = 0;
        let inactCount = 0;
        results.forEach((v) => {
          const st = String(v?.tracker_status || '').toUpperCase();
          if (st === 'RUNNING') runCount++;
          else if (st === 'IDLE') idleCount++;
          else if (st === 'STOP') stopCount++;
          else inactCount++;
        });

        if (isMounted) {
          setCountsData({
            total,
            running: runCount,
            idle: idleCount,
            stop: stopCount,
            inactive: inactCount,
          });
        }
      } catch (err) {
        console.error("Failed to fetch vehicle status counts:", err);
      }
    };
    fetchCounts();
    return () => {
      isMounted = false;
    };
  }, []);

  // Vehicle Type is a dynamic, admin-managed list - fetch it from the real dropdown endpoint
  // instead of hardcoding titles.
  useEffect(() => {
    let isMounted = true;
    const fetchVehicleTypes = async () => {
      try {
        const res = await apiService.getVehicleTypesDropdown();
        const rows = res?.data || res || [];
        if (isMounted) {
          setVehicleTypeOptions((Array.isArray(rows) ? rows : []).map((row) => ({
            value: row.value ?? row.id,
            label: row.label || row.title || String(row.value ?? row.id),
          })));
        }
      } catch (err) {
        console.error('Failed to fetch vehicle types dropdown:', err);
      }
    };
    fetchVehicleTypes();
    return () => {
      isMounted = false;
    };
  }, []);

  // Fetch paginated vehicles from /fmcr-statuses/vehicle-paginated, filtered by whichever of
  // Vehicle Type / Ownership Type / Status the user has selected via the table's filter chips.
  const fetchVehicleStatuses = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page: currentPage,
        limit: limit,
        search_text: searchValue,
        searchFilters,
      };

      const response = await apiService.getFmcrStatusesVehiclePaginated(params);
      const dataObj = response?.data || response || {};
      const results = dataObj?.results || dataObj?.rows || (Array.isArray(dataObj) ? dataObj : []);
      const total = Number(dataObj?.meta?.totalItems ?? dataObj?.total ?? results.length) || 0;

      setVehiclesData(results);
      setTotalItems(total);
    } catch (error) {
      console.error("Failed to fetch vehicle statuses:", error);
      setVehiclesData([]);
      setTotalItems(0);
    } finally {
      setLoading(false);
    }
  }, [currentPage, limit, searchValue, searchFilters]);

  useEffect(() => {
    fetchVehicleStatuses();
  }, [fetchVehicleStatuses]);

  // Status overview cards - all 11 cards per the design. Only "Total Vehicles" and "Moving" have a
  // real backing field today (last_log.status === 'RUNNING'); the rest are trip-lifecycle statuses
  // the API doesn't track yet, so they honestly show 0 rather than a fabricated number.
  const statsCards = useMemo(() => {
    const total = countsData.total || totalItems || 0;
    const moving = countsData.running || 0;
    const calcPct = (count) => (total > 0 ? `${Math.round((count / total) * 100)}%` : '0%');

    return [
      { key: 'total', title: 'Total Vehicles', value: total, percent: '', icon: <FiTruck />, iconBg: '#F8F6FE', iconColor: '#3F14B5' },
      { key: 'awaiting', title: 'Awaiting for Trip', value: 0, percent: calcPct(0), icon: <FiClock />, iconBg: '#FEF3C7', iconColor: '#D97706' },
      { key: 'tripStarted', title: 'Trip Started', value: 0, percent: calcPct(0), icon: <FiFlag />, iconBg: '#F3E8FF', iconColor: '#9333EA' },
      { key: 'loading', title: 'Loading', value: 0, percent: calcPct(0), icon: <FiDownload />, iconBg: '#FEF08A', iconColor: '#CA8A04' },
      { key: 'moving', title: 'Moving', value: moving, percent: calcPct(moving), icon: <FiPlay />, iconBg: '#DCFCE7', iconColor: '#16A34A' },
      { key: 'reachedDestination', title: 'Reached Destination', value: 0, percent: calcPct(0), icon: <FiMapPin />, iconBg: '#D1FAE5', iconColor: '#059669' },
      { key: 'unloading', title: 'Unloading', value: 0, percent: calcPct(0), icon: <FiUpload />, iconBg: '#F3E8FF', iconColor: '#A855F7' },
      { key: 'onwayBack', title: 'Onway Back', value: 0, percent: calcPct(0), icon: <FiCornerDownLeft />, iconBg: '#F1F5F9', iconColor: '#64748B' },
      { key: 'reachedOrigin', title: 'Reached Origin', value: 0, percent: calcPct(0), icon: <FiHome />, iconBg: '#F1F5F9', iconColor: '#475569' },
      { key: 'maintenance', title: 'Maintenance', value: 0, percent: calcPct(0), icon: <FiTool />, iconBg: '#FFEDD5', iconColor: '#EA580C' },
      { key: 'documentation', title: 'Documentation', value: 0, percent: calcPct(0), icon: <FiFileText />, iconBg: '#F3F4F6', iconColor: '#6B7280' },
    ];
  }, [countsData, totalItems]);

  const commonColumns = {
    sortable: false,
    filterName: "",
  };

  // Table column configuration. Only 3 filter chips exist right now, per the confirmed real
  // filter contract of /fmcr-statuses/vehicle-paginated: Vehicle Type, Ownership Type, Status
  // (plus the search box for search_text). Other columns still show real data where available,
  // they're just not filterable yet.
  const columns = useMemo(() => [
    {
      ...commonColumns,
      name: "Registration No.",
      selector: (row) => row.registration_number ? String(row.registration_number).toUpperCase() : "-",
      cell: (row) => (
        <span
          className="hyperlink"
          role="button"
          tabIndex={0}
          onClick={() => row?.id && navigate(`/assets/vehicles/${row.id}`)}
        >
          {row.registration_number ? String(row.registration_number).toUpperCase() : "-"}
        </span>
      ),
      minWidth: "170px",
    },
    {
      ...commonColumns,
      name: "Type",
      selector: (row) => row.vehicle_type?.title || "-",
      cell: (row) => <span style={{ whiteSpace: 'nowrap' }}>{row.vehicle_type?.title || "-"}</span>,
      minWidth: "150px",
      tableColumn: true,
      filterName: "vehicleTypeFilter",
      filterComponent: FilterExternalDropdown,
      isMulti: false,
      options: vehicleTypeOptions,
    },
    {
      ...commonColumns,
      name: "Ownership",
      selector: (row) => row.ownership_type || "-",
      cell: (row) => {
        const label = row.ownership_type
          ? row.ownership_type.charAt(0).toUpperCase() + row.ownership_type.slice(1).toLowerCase()
          : "-";
        return <span>{label}</span>;
      },
      minWidth: "130px",
      tableColumn: true,
      filterName: "ownershipTypeFilter",
      filterComponent: FilterExternalDropdown,
      isMulti: false,
      options: OWNERSHIP_TYPE_FILTER_OPTIONS,
    },
    {
      ...commonColumns,
      name: "Current Status",
      selector: (row) => row.tracker_status || "-",
      cell: (row) => {
        if (!row.tracker_status) return <span>-</span>;
        const meta = vehicleStatusMeta(row.tracker_status);
        return (
          <span className="transporter_status_cell" style={{ color: meta.color }}>
            {meta.icon}
            {meta.label}
          </span>
        );
      },
      minWidth: "150px",
      tableColumn: true,
      filterName: "statusFilter",
      filterComponent: FilterExternalDropdown,
      isMulti: false,
      options: STATUS_FILTER_OPTIONS,
    },
    {
      ...commonColumns,
      name: "Current Location",
      selector: (row) => row.last_log?.location || "-",
      cell: (row) => (
        <span
          title={row.last_log?.location || ""}
          style={{
            maxWidth: "220px",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            display: "inline-block",
            color: "#334155",
          }}
        >
          {row.last_log?.location || "-"}
        </span>
      ),
      minWidth: "220px",
    },
    {
      name: "Customer",
      selector: (row) => row.on_going_trip?.customer?.name || "-",
      cell: (row) => <span>{row.on_going_trip?.customer?.name || "-"}</span>,
      minWidth: "140px",
    },
    {
      name: "Cost Center",
      selector: () => "-",
      cell: () => <span>-</span>,
      minWidth: "140px",
    },
    {
      name: "Capacity",
      selector: () => "-",
      cell: () => <span>-</span>,
      minWidth: "110px",
    },
    {
      name: "Last Trip End",
      selector: () => "-",
      cell: () => <span>-</span>,
      minWidth: "150px",
    },
    {
      name: "Current Trip Start",
      selector: () => "-",
      cell: () => <span>-</span>,
      minWidth: "170px",
    },
    {
      name: "Idle Time",
      selector: () => "-",
      cell: () => <span>-</span>,
      minWidth: "110px",
    },
    {
      name: "Origin",
      selector: (row) => row.on_going_trip?.start_trip?.location || "-",
      cell: (row) => (
        <span
          title={row.on_going_trip?.start_trip?.location || ""}
          style={{ maxWidth: "160px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", display: "inline-block" }}
        >
          {row.on_going_trip?.start_trip?.location || "-"}
        </span>
      ),
      minWidth: "150px",
    },
    {
      name: "Destination",
      selector: (row) => row.on_going_trip?.end_trip?.location || "-",
      cell: (row) => (
        <span
          title={row.on_going_trip?.end_trip?.location || ""}
          style={{ maxWidth: "180px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", display: "inline-block" }}
        >
          {row.on_going_trip?.end_trip?.location || "-"}
        </span>
      ),
      minWidth: "170px",
    },
    {
      name: "Remarks",
      selector: () => "-",
      cell: () => <span>-</span>,
      minWidth: "150px",
    },
    {
      ...commonColumns,
      name: "Last Update",
      selector: (row) => row.last_log?.created_at || "-",
      cell: (row) => {
        const dateStr = row.last_log?.created_at;
        return (
          <span style={{ fontSize: '12px', color: '#64748b', whiteSpace: 'nowrap' }}>
            {dateStr ? formatLocalDateTime(dateStr) : "-"}
          </span>
        );
      },
      minWidth: "170px",
    },
    {
      name: "Actions",
      selector: () => "",
      cell: () => (
        <div className="vst_action_cell">
          <button type="button" className="vst_row_action_btn" title="Edit (coming soon)">
            <FiEdit2 />
          </button>
          <button type="button" className="vst_row_action_btn" title="History (coming soon)">
            <FiClock />
          </button>
        </div>
      ),
      minWidth: "110px",
    },
  ], [navigate, vehicleTypeOptions]);

  const totalPages = Math.max(Math.ceil(totalItems / limit), 1);

  const handleSearch = (text) => {
    setSearchValue(text);
    setCurrentPage(1);
  };


  return (
    <div>
      <Breadcrumbs values={["FMCR", "Vehicle Status"]} />

      <div className="access_view">
        <div className="d-flex align-items-center gap-3">
          <h2>Vehicle Status</h2>
        </div>

        <div className="services_buttons">
          <button type="button" onClick={() => setShowAddEntry(true)}>
            {plusIcon} Add Entry
          </button>
        </div>
      </div>

      {/* Status Overview Cards */}
      <div className="vst_kpi_grid">
        {statsCards.map((stat) => (
          <div key={stat.key} className="vst_kpi_card">
            <div className="vst_kpi_card_header">{stat.title}</div>
            <div className="vst_kpi_card_body">
              <div className="vst_kpi_icon_badge" style={{ background: stat.iconBg, color: stat.iconColor }}>
                {stat.icon}
              </div>
              <div className="vst_kpi_stat_values">
                <div className="vst_kpi_divider" />
                <span className="vst_kpi_number">{stat.value}</span>
                {stat.percent && <span className="vst_kpi_percent">{stat.percent}</span>}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Main Table */}
      <NewTableComponent
        columns={columns}
        data={vehiclesData}
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        limit={limit}
        setLimit={setLimit}
        totalItems={totalItems}
        totalPages={totalPages}
        onSearch={handleSearch}
        searchValue={searchValue}
        setSearchFilters={setSearchFilters}
        searchFilters={searchFilters}
        loading={loading}
        showFilter={true}
        showAddBtn={false}
        tableClassName="transporters_table"
      />

      <CustomModal
        heading="Add Fleet Status Entry"
        size="xl"
        show={showAddEntry}
        handleClose={() => setShowAddEntry(false)}
      >
        <AddFleetStatusEntry onClose={() => setShowAddEntry(false)} />
      </CustomModal>
    </div>
  );
};

export default VehicleStatus;
